import FinancialEventRevenueEntry from '@/components/event/FinancialEventRevenueEntry';
// File: src/pages/Event/EventDetailPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/axios";
import toast from "react-hot-toast";
import {
  CalendarDays,
  Clock3,
  MapPin,
  User2,
  Users,
  Package,
  TrendingUp,
  RefreshCw,
  FileDown,
  Sheet,
  LayoutGrid,
  Briefcase,
  ClipboardList,
  BarChart3,
  Wallet,
  AlertTriangle,
  CheckCircle2,
  Pencil,
  UtensilsCrossed,
  PackageSearch,
  Receipt,
  Activity,
  Banknote,
  ClipboardCheck,
} from "lucide-react";

import EventMenuOverview from "@/components/event/EventMenuOverview";
import EventStaffingOverview from "@/components/event/EventStaffingOverview";
import EventProcurementOverview from "@/components/event/EventProcurementOverview";
import EventManagerReportViewer from "@/components/event/EventManagerReportViewer";
import EventBillingPanel from "@/components/event/EventBillingPanel";

import { useUserPermissions } from "@/hooks/useUserPermissions";

/* ───────────────── helpers ───────────────── */
const toNum = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

const toNumNull = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const gbp = (v) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(toNum(v, 0));

const safeStr = (v, d = "—") => {
  const s = v == null ? "" : String(v).trim();
  return s ? s : d;
};

const pct = (v, d = "—") => {
  const n = toNumNull(v);
  return n === null ? d : `${n.toFixed(1)}%`;
};

function pickFirst(obj, keys, fallback = null) {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return fallback;
}

function asDateGB(v) {
  try {
    if (!v) return "—";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v).slice(0, 10);
    return d.toLocaleDateString("en-GB");
  } catch {
    return v ? String(v).slice(0, 10) : "—";
  }
}

function asDateTimeGB(v) {
  try {
    if (!v) return "—";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v).slice(0, 19).replace("T", " ");
    return d.toLocaleString("en-GB");
  } catch {
    return v ? String(v).slice(0, 19).replace("T", " ") : "—";
  }
}

function asDateTimeLocalValue(v) {
  try {
    if (!v) return "";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (x) => String(x).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
      d.getMinutes()
    )}`;
  } catch {
    return "";
  }
}

function dateTimeLocalToIso(v) {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function normalizeMoneyInput(v) {
  const s = String(v ?? "").trim();
  if (!s) return "";
  return s.replace(/[^\d.-]/g, "");
}

function sumBy(arr, getter) {
  return (Array.isArray(arr) ? arr : []).reduce((acc, item) => acc + toNum(getter(item), 0), 0);
}

function valueToneClass(v) {
  const n = toNumNull(v);
  if (n === null) return "text-slate-900";
  if (n > 0) return "text-emerald-700";
  if (n < 0) return "text-rose-700";
  return "text-slate-900";
}

function varianceBgClass(v) {
  const n = toNumNull(v);
  if (n === null) return "bg-white";
  if (n > 0) return "bg-emerald-50 border-emerald-200";
  if (n < 0) return "bg-rose-50 border-rose-200";
  return "bg-slate-50 border-slate-200";
}

/* ───────── menu line normalization + robust field pickers ───────── */
function normalizeMenuLines(details) {
  const candidates = [
    details?.menuItems,
    details?.menu_items,
    details?.menu_lines,
    details?.menuLines,
    details?.lines,
    details?.menu_plan_lines,
    details?.menuPlanLines,
    details?.forecast?.menu_items,
  ];

  for (const c of candidates) {
    if (Array.isArray(c)) return c;
  }
  return [];
}

function lineQty(l) {
  return toNum(
    pickFirst(l, ["qty", "quantity", "servings", "expected_servings", "portions", "portion_qty"], 0),
    0
  );
}

function lineUnitPrice(l) {
  return toNum(pickFirst(l, ["unit_price", "event_sale_price", "sell_price", "price"], 0), 0);
}

function lineUnitCost(l) {
  return toNum(
    pickFirst(l, ["unit_cost", "unit_cost_est", "unit_cost_estimated", "cost", "cost_est"], 0),
    0
  );
}

function buildSummaryFromDetails(details, menuLines) {
  const f = details?.forecast || {};
  const quoteTotal =
    toNumNull(f.client_total_quote) ??
    toNumNull(f.client_total_final) ??
    toNumNull(f.client_total) ??
    0;

  const cogs = toNum(f.cogs_total ?? f.cogs_estimated, 0);
  const gross_profit = quoteTotal - cogs;
  const cogs_pct = quoteTotal > 0 ? (cogs / quoteTotal) * 100 : null;

  const margin_value = toNum(f.margin_value ?? gross_profit, 0);
  const margin_pct = Number.isFinite(Number(f.margin_pct))
    ? toNum(f.margin_pct, 0)
    : quoteTotal > 0
      ? (gross_profit / quoteTotal) * 100
      : null;

  const portions = toNum(f.portions, 0);

  const menu_items_count =
    Number.isFinite(Number(f.menu_items_count)) && toNum(f.menu_items_count, 0) > 0
      ? toNum(f.menu_items_count, 0)
      : menuLines.length;

  const staffing_cost = toNum(
    f.staffing_cost_effective ?? f.staffing_cost ?? details?.staffingForecast?.effective_staffing_cost,
    0
  );

  return {
    quoteTotal,
    cogs,
    gross_profit,
    cogs_pct,
    margin_value,
    margin_pct,
    portions,
    menu_items_count,
    staffing_cost,
  };
}

/* ───────── details fetch (canonical + minimal fallback) ───────── */
async function getEventDetailsWithFallbacks(id, { signal } = {}) {
  try {
    const r = await api.get(`/event/${id}/details`, { signal });
    return r.data;
  } catch (e1) {
    if (e1?.response?.status !== 404) {
      const msg = e1?.response?.data?.error || e1?.message || "Unable to load event details";
      throw new Error(msg);
    }

    try {
      const r2 = await api.get(`/events/${id}/details`, { signal });
      return r2.data;
    } catch (e2) {
      const msg =
        e2?.response?.data?.error ||
        e1?.response?.data?.error ||
        e2?.message ||
        "Unable to load event details";
      throw new Error(msg);
    }
  }
}

/* ───────── Procurement (canonical purchasing cost) ───────── */
function normalizeProcurementPayload(data) {
  const d = data || {};
  return {
    headline: d.headline || null,
    summary: d.summary || {},
    items: Array.isArray(d.items) ? d.items : [],
    purchase_orders: Array.isArray(d.purchase_orders) ? d.purchase_orders : [],
    coverage: d.coverage || {},
    notes: d.notes || {},
  };
}

async function getEventProcurementWithFallbacks(eventId, { signal } = {}) {
  try {
    const r = await api.get(`/event/${eventId}/procurement`, { signal });
    return { ok: true, data: normalizeProcurementPayload(r.data), used: "/event/:eventId/procurement" };
  } catch (e1) {
    if (e1?.response?.status === 404) {
      try {
        const r2 = await api.get(`/events/${eventId}/procurement`, { signal });
        return { ok: true, data: normalizeProcurementPayload(r2.data), used: "/events/:eventId/procurement" };
      } catch (e2) {
        if (e2?.response?.status === 404) {
          return {
            ok: true,
            data: {
              headline: null,
              summary: {},
              items: [],
              purchase_orders: [],
              coverage: {},
              notes: {},
            },
            used: null,
            unavailable: true,
          };
        }
        throw e2;
      }
    }
    throw e1;
  }
}

/* ───────── Revenue (Actuals) canonical + minimal fallback ───────── */
function normalizeRevenuePayload(data) {
  const d = data || {};
  if (d.revenue && typeof d.revenue === "object") return d.revenue;
  if (d.data && typeof d.data === "object") return d.data;
  if (d.actual_food_revenue != null || d.actual_beverage_revenue != null) return d;
  return null;
}

async function getEventRevenueWithFallbacks(eventId, { signal } = {}) {
  try {
    const r = await api.get(`/event/revenue/${eventId}`, { signal });
    return { ok: true, revenue: normalizeRevenuePayload(r.data), used: "/event/revenue/:eventId" };
  } catch (e1) {
    if (e1?.response?.status === 404) {
      try {
        const r2 = await api.get(`/events/revenue/${eventId}`, { signal });
        return { ok: true, revenue: normalizeRevenuePayload(r2.data), used: "/events/revenue/:eventId" };
      } catch (e2) {
        if (e2?.response?.status === 404) {
          return { ok: true, revenue: null, used: null };
        }
        throw e2;
      }
    }
    throw e1;
  }
}

async function postEventRevenueWithFallbacks(eventId, payload) {
  try {
    const r = await api.post(`/event/revenue`, { event_id: eventId, ...payload });
    return { ok: true, revenue: normalizeRevenuePayload(r.data), used: "/event/revenue" };
  } catch (e1) {
    if (e1?.response?.status === 404) {
      const r2 = await api.post(`/events/revenue`, { event_id: eventId, ...payload });
      return { ok: true, revenue: normalizeRevenuePayload(r2.data), used: "/events/revenue" };
    }
    throw e1;
  }
}

/* ───────── Staffing costs by cost centre ───────── */
function normalizeCostCentreRow(row) {
  if (!row || typeof row !== "object") return null;
  const id = toNumNull(row.cost_centre_id) ?? toNumNull(row.id) ?? toNumNull(row.costCentreId);

  if (id == null) return null;

  return {
    cost_centre_id: Number(id),
    id: Number(id),
    code: safeStr(row.code, ""),
    name:
      safeStr(row.name, "") ||
      safeStr(row.cost_centre_name, "") ||
      safeStr(row.centre_name, "") ||
      `Cost Centre ${id}`,
    is_active: row.is_active !== false,
  };
}

function normalizeStaffingCostRow(row) {
  if (!row || typeof row !== "object") return null;

  const costCentreId =
    toNumNull(row.cost_centre_id) ??
    toNumNull(row.costCentreId) ??
    toNumNull(row.id);

  if (costCentreId == null) return null;

  return {
    id: toNumNull(row.id),
    cost_centre_id: Number(costCentreId),
    amount: toNum(row.amount, 0),
    notes: safeStr(row.notes, ""),
    submitted_at: row.submitted_at || row.created_at || null,
    submitted_by: row.submitted_by ?? null,
    cost_centre_name: safeStr(row.cost_centre_name, ""),
    cost_centre_code: safeStr(row.cost_centre_code, ""),
  };
}

function normalizeStaffingCostsPayload(data) {
  const d = data || {};

  const forecastRowsRaw =
    d.forecast_rows ||
    d.forecastRows ||
    d.forecast ||
    d.forecast_costs ||
    d.forecastCosts ||
    [];

  const actualRowsRaw =
    d.actual_rows ||
    d.actualRows ||
    d.actual ||
    d.actual_costs ||
    d.actualCosts ||
    [];

  const costCentresRaw =
    d.cost_centres ||
    d.costCentres ||
    d.available_cost_centres ||
    d.availableCostCentres ||
    d.centres ||
    [];

  const forecastRows = (Array.isArray(forecastRowsRaw) ? forecastRowsRaw : [])
    .map(normalizeStaffingCostRow)
    .filter(Boolean);

  const actualRows = (Array.isArray(actualRowsRaw) ? actualRowsRaw : [])
    .map(normalizeStaffingCostRow)
    .filter(Boolean);

  const baseCostCentres = (Array.isArray(costCentresRaw) ? costCentresRaw : [])
    .map(normalizeCostCentreRow)
    .filter(Boolean);

  const derivedFromRows = [...forecastRows, ...actualRows]
    .map((row) =>
      normalizeCostCentreRow({
        cost_centre_id: row.cost_centre_id,
        code: row.cost_centre_code,
        name: row.cost_centre_name,
      })
    )
    .filter(Boolean);

  const mergedCostCentres = Array.from(
    new Map([...baseCostCentres, ...derivedFromRows].map((c) => [Number(c.cost_centre_id), c])).values()
  );

  const forecastTotal =
    toNumNull(d.forecast_total) ??
    toNumNull(d.forecastTotal) ??
    sumBy(forecastRows, (r) => r.amount);

  const actualTotal =
    toNumNull(d.actual_total) ??
    toNumNull(d.actualTotal) ??
    sumBy(actualRows, (r) => r.amount);

  return {
    forecastRows,
    actualRows,
    costCentres: mergedCostCentres,
    forecastTotal: toNum(forecastTotal, 0),
    actualTotal: toNum(actualTotal, 0),
  };
}

async function getEventStaffingCostsWithFallbacks(eventId, { signal } = {}) {
  const tries = [
    `/event/${eventId}/staffing-costs`,
    `/event/${eventId}/staffing/costs`,
    `/events/${eventId}/staffing-costs`,
    `/events/${eventId}/staffing/costs`,
  ];

  let lastErr = null;

  for (const url of tries) {
    try {
      const r = await api.get(url, { signal });
      return { ok: true, data: normalizeStaffingCostsPayload(r.data), used: url };
    } catch (e) {
      lastErr = e;
      if (e?.response?.status === 404) continue;
      throw e;
    }
  }

  if (lastErr?.response?.status === 404) {
    return {
      ok: true,
      data: {
        forecastRows: [],
        actualRows: [],
        costCentres: [],
        forecastTotal: 0,
        actualTotal: 0,
      },
      used: null,
      unavailable: true,
    };
  }

  throw lastErr;
}

async function postEventStaffingActualsWithFallbacks(eventId, payload) {
  const tries = [
    {
      url: `/event/${eventId}/staffing-costs/actuals`,
      body: {
        staffing_actual_lines: payload.staffing_actual_lines,
        replace: payload.replace ?? true,
      },
    },
    {
      url: `/event/${eventId}/staffing/actual-costs`,
      body: {
        staffing_actual_lines: payload.staffing_actual_lines,
        replace: payload.replace ?? true,
      },
    },
    {
      url: `/events/${eventId}/staffing-costs/actuals`,
      body: {
        staffing_actual_lines: payload.staffing_actual_lines,
        replace: payload.replace ?? true,
      },
    },
    {
      url: `/events/${eventId}/staffing/actual-costs`,
      body: {
        staffing_actual_lines: payload.staffing_actual_lines,
        replace: payload.replace ?? true,
      },
    },
  ];

  let lastErr = null;

  for (const item of tries) {
    try {
      const r = await api.post(item.url, item.body);
      return { ok: true, data: normalizeStaffingCostsPayload(r.data), used: item.url };
    } catch (e) {
      lastErr = e;
      if (e?.response?.status === 404) continue;
      throw e;
    }
  }

  if (lastErr?.response?.status === 404) {
    throw new Error("Staffing cost actuals endpoint is not available yet.");
  }

  throw lastErr;
}

/* ───────── Commercials canonical + minimal fallback ───────── */
function normalizeCommercialPayload(data) {
  const d = data || {};

  if (d.commercial && typeof d.commercial === "object") {
    return {
      header: d.commercial.header || null,
      totals: d.commercial.totals || {},
      transactions: Array.isArray(d.commercial.transactions) ? d.commercial.transactions : [],
    };
  }

  if (d.header || d.totals || d.transactions) {
    return {
      header: d.header || null,
      totals: d.totals || {},
      transactions: Array.isArray(d.transactions) ? d.transactions : [],
    };
  }

  if (d.data && typeof d.data === "object") {
    return normalizeCommercialPayload(d.data);
  }

  if (d.event_id != null || d.quote_status != null || d.client_total_quote != null) {
    return {
      header: d,
      totals: {},
      transactions: [],
    };
  }

  return null;
}

function normalizeTxPayload(data) {
  const d = data || {};
  const list = d.transactions ?? d.items ?? d.rows ?? d.data ?? d;

  if (Array.isArray(list)) return list;
  if (list && typeof list === "object" && (list.id != null || (list.event_id != null && list.amount != null))) {
    return [list];
  }
  return [];
}

async function getEventCommercialWithFallbacks(eventId, { signal } = {}) {
  try {
    const r = await api.get(`/event/${eventId}/commercial`, { signal });
    return { ok: true, commercial: normalizeCommercialPayload(r.data), used: "/event/:eventId/commercial" };
  } catch (e1) {
    if (e1?.response?.status === 404) {
      try {
        const r2 = await api.get(`/events/${eventId}/commercial`, { signal });
        return { ok: true, commercial: normalizeCommercialPayload(r2.data), used: "/events/:eventId/commercial" };
      } catch (e2) {
        if (e2?.response?.status === 404) {
          return { ok: true, commercial: null, used: null };
        }
        throw e2;
      }
    }
    throw e1;
  }
}

async function putEventCommercialWithFallbacks(eventId, payload) {
  try {
    const r = await api.put(`/event/${eventId}/commercial`, payload);
    return { ok: true, commercial: normalizeCommercialPayload(r.data), used: "/event/:eventId/commercial" };
  } catch (e1) {
    if (e1?.response?.status === 404) {
      const r2 = await api.put(`/events/${eventId}/commercial`, payload);
      return { ok: true, commercial: normalizeCommercialPayload(r2.data), used: "/events/:eventId/commercial" };
    }
    throw e1;
  }
}

async function getEventCommercialTxWithFallbacks(eventId, { signal } = {}) {
  try {
    const r = await api.get(`/event/${eventId}/commercial/transactions`, { signal });
    return { ok: true, transactions: normalizeTxPayload(r.data), used: "/event/:eventId/commercial/transactions" };
  } catch (e1) {
    if (e1?.response?.status === 404) {
      try {
        const r2 = await api.get(`/events/${eventId}/commercial/transactions`, { signal });
        return { ok: true, transactions: normalizeTxPayload(r2.data), used: "/events/:eventId/commercial/transactions" };
      } catch (e2) {
        if (e2?.response?.status === 404) {
          return { ok: true, transactions: [], used: null };
        }
        throw e2;
      }
    }
    throw e1;
  }
}

async function postEventCommercialTxWithFallbacks(eventId, payload) {
  try {
    const r = await api.post(`/event/${eventId}/commercial/transactions`, payload);
    const txs = normalizeTxPayload(r.data);
    return {
      ok: true,
      transaction: txs?.[0] ?? null,
      transactions: txs,
      used: "/event/:eventId/commercial/transactions",
    };
  } catch (e1) {
    if (e1?.response?.status === 404) {
      const r2 = await api.post(`/events/${eventId}/commercial/transactions`, payload);
      const txs = normalizeTxPayload(r2.data);
      return {
        ok: true,
        transaction: txs?.[0] ?? null,
        transactions: txs,
        used: "/events/:eventId/commercial/transactions",
      };
    }
    throw e1;
  }
}

/* ───────── binary downloads ───────── */
function getHeaderValue(headers, key) {
  if (!headers || !key) return null;

  try {
    if (typeof headers.get === "function") {
      const viaGet = headers.get(key);
      if (viaGet) return viaGet;
    }
  } catch {
    // noop
  }

  const target = String(key).toLowerCase();
  const entries = Object.entries(headers || {});
  for (const [k, v] of entries) {
    if (String(k).toLowerCase() === target) return v;
  }

  return null;
}

function parseFilenameFromContentDisposition(contentDisposition, fallbackName) {
  const raw = String(contentDisposition || "").trim();
  if (!raw) return fallbackName;

  const utf8Match = raw.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]).replace(/[/\\?%*:|"<>]/g, "-");
    } catch {
      return utf8Match[1].replace(/[/\\?%*:|"<>]/g, "-");
    }
  }

  const basicMatch = raw.match(/filename\s*=\s*"?([^";]+)"?/i);
  if (basicMatch?.[1]) {
    return basicMatch[1].replace(/[/\\?%*:|"<>]/g, "-");
  }

  return fallbackName;
}

function isSafariLike() {
  if (typeof navigator === "undefined") return false;
  const ua = String(navigator.userAgent || "");
  const vendor = String(navigator.vendor || "");
  const isSafariEngine =
    /Safari/i.test(ua) &&
    !/Chrome|CriOS|Chromium|Edg|OPR|Firefox|FxiOS|SamsungBrowser|Android/i.test(ua);
  return isSafariEngine || /Apple/i.test(vendor);
}

function isMacLike() {
  if (typeof navigator === "undefined") return false;
  const platform = String(navigator.platform || "");
  const ua = String(navigator.userAgent || "");
  return /Mac/i.test(platform) || /Mac OS/i.test(ua);
}

function buildDownloadUrlWithCacheBust(url) {
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}_ts=${Date.now()}`;
}

function triggerAnchorDownload(objectUrl, filename) {
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();

  setTimeout(() => {
    try {
      a.remove();
    } catch {
      // noop
    }
  }, 0);
}

function openBlobUrlInNewTab(objectUrl) {
  const opened = window.open(objectUrl, "_blank", "noopener,noreferrer");
  return !!opened;
}

async function downloadBinaryWithFallbacks(urls, filename, mimeType, options = {}) {
  let lastErr = null;

  const { preferInlineOpenOnSafari = false, allowedContentTypes = [] } = options;

  for (const rawUrl of urls) {
    try {
      const url = buildDownloadUrlWithCacheBust(rawUrl);

      const res = await api.get(url, {
        responseType: "arraybuffer",
        headers: {
          Accept: mimeType,
        },
      });

      const responseHeaders = res?.headers || {};
      const contentTypeRaw = String(getHeaderValue(responseHeaders, "content-type") || mimeType || "").trim();
      const contentDisposition = getHeaderValue(responseHeaders, "content-disposition");

      const contentType = contentTypeRaw.split(";")[0].trim().toLowerCase();

      const isAcceptedType =
        contentType === mimeType.toLowerCase() ||
        allowedContentTypes.map((x) => String(x).toLowerCase()).includes(contentType);

      if (!isAcceptedType) {
        if (contentType.includes("text/html")) {
          throw new Error("Export endpoint returned HTML instead of a file.");
        }
        if (contentType.includes("application/json")) {
          throw new Error("Export endpoint returned JSON instead of a file.");
        }
      }

      const fileNameFromHeader = parseFilenameFromContentDisposition(contentDisposition, filename);
      const data = res?.data;

      const blob = new Blob([data], {
        type: contentType || mimeType,
      });

      if (!blob || blob.size === 0) {
        throw new Error("Downloaded file is empty.");
      }

      const href = URL.createObjectURL(blob);

      const safariPdfMode =
        preferInlineOpenOnSafari &&
        mimeType === "application/pdf" &&
        isSafariLike() &&
        isMacLike();

      if (safariPdfMode) {
        const opened = openBlobUrlInNewTab(href);

        if (!opened) {
          const a = document.createElement("a");
          a.href = href;
          a.target = "_blank";
          a.rel = "noopener noreferrer";
          a.style.display = "none";
          document.body.appendChild(a);
          a.click();

          setTimeout(() => {
            try {
              a.remove();
            } catch {
              // noop
            }
          }, 0);
        }
      } else {
        triggerAnchorDownload(href, fileNameFromHeader || filename);
      }

      setTimeout(() => {
        try {
          URL.revokeObjectURL(href);
        } catch {
          // noop
        }
      }, 60000);

      return {
        ok: true,
        used: rawUrl,
        size: blob.size,
        filename: fileNameFromHeader || filename,
        contentType,
      };
    } catch (e) {
      lastErr = e;
      if (e?.response?.status === 404) continue;
      throw e;
    }
  }

  if (lastErr?.response?.status === 404) {
    throw new Error("Export endpoint is not available yet for this event.");
  }

  throw new Error(lastErr?.response?.data?.error || lastErr?.message || "Export failed");
}

async function downloadEventPdfWithFallbacks(eventId, fileNameBase) {
  return downloadBinaryWithFallbacks(
    [
      `/event/${eventId}/report/pdf`,
      `/events/${eventId}/report/pdf`,
      `/event/${eventId}/pdf`,
      `/events/${eventId}/pdf`,
    ],
    `${fileNameBase || `event-${eventId}`}.pdf`,
    "application/pdf",
    {
      preferInlineOpenOnSafari: true,
      allowedContentTypes: ["application/octet-stream"],
    }
  );
}

async function downloadEventExcelWithFallbacks(eventId, fileNameBase) {
  return downloadBinaryWithFallbacks(
    [
      `/event/${eventId}/report/excel`,
      `/event/${eventId}/report/xlsx`,
      `/events/${eventId}/report/excel`,
      `/events/${eventId}/report/xlsx`,
      `/event/${eventId}/excel`,
      `/event/${eventId}/xlsx`,
      `/events/${eventId}/excel`,
      `/events/${eventId}/xlsx`,
    ],
    `${fileNameBase || `event-${eventId}`}.xlsx`,
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    {
      allowedContentTypes: ["application/octet-stream", "application/vnd.ms-excel"],
    }
  );
}

/* ───────────────── ErrorBoundary ───────────────── */
class SectionErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { err: null };
  }

  static getDerivedStateFromError(err) {
    return { err };
  }

  componentDidCatch(err, info) {
    console.error("Section crashed:", err, info);
  }

  render() {
    if (this.state.err) {
      const title = this.props.title || "Section";
      return (
        <div className="rounded-2xl border border-rose-300 bg-rose-50 p-4">
          <div className="font-semibold text-rose-700">{title} crashed</div>
          <div className="mt-1 text-sm text-rose-600">
            {this.state.err?.message || "A rendering error occurred."}
          </div>
          <div className="mt-2 text-xs text-rose-500">Open DevTools → Console for stack.</div>
          {this.props.onRetry ? (
            <button
              onClick={() => {
                this.setState({ err: null });
                this.props.onRetry();
              }}
              className="mt-3 rounded-lg border border-rose-200 bg-rose-600 px-3 py-1.5 text-xs text-white hover:bg-rose-700"
              type="button"
            >
              Retry
            </button>
          ) : null}
        </div>
      );
    }
    return this.props.children;
  }
}

/* ───────────────── Main ───────────────── */
export default function EventDetailPage() {
  const params = useParams();
  const rawId = params?.id ?? params?.eventId ?? null;
  const navigate = useNavigate();

  const {
    user,
    canViewEventStaffing,
    canViewEventProcurement,
    canViewEventMenu,
    canViewEventBilling,
    canViewEventReports,
  } = useUserPermissions();

  const eventIdNum = useMemo(() => {
    const n = Number(rawId);
    return Number.isFinite(n) ? n : null;
  }, [rawId]);

  const [details, setDetails] = useState(null);

  const [procurement, setProcurement] = useState({
    headline: null,
    summary: {},
    items: [],
    purchase_orders: [],
    coverage: {},
    notes: {},
  });
  const [procurementLoaded, setProcurementLoaded] = useState(false);

  const [revenue, setRevenue] = useState(null);
  const [revenueLoaded, setRevenueLoaded] = useState(false);

  const [staffingCosts, setStaffingCosts] = useState({
    forecastRows: [],
    actualRows: [],
    costCentres: [],
    forecastTotal: 0,
    actualTotal: 0,
  });
  const [staffingCostsLoaded, setStaffingCostsLoaded] = useState(false);

  const [commercial, setCommercial] = useState(null);
  const [commercialTx, setCommercialTx] = useState([]);
  const [commercialLoaded, setCommercialLoaded] = useState(false);

  const [initialLoad, setInitialLoad] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [tab, setTab] = useState("overview");
  const [pdfBusy, setPdfBusy] = useState(false);
  const [excelBusy, setExcelBusy] = useState(false);
  const [retryTick, setRetryTick] = useState(0);

  const reqSeq = useRef(0);

  const tabs = useMemo(() => {
    const base = [
      { key: "overview", label: "Overview", icon: LayoutGrid },
      { key: "manager", label: "Manager", icon: Briefcase },
    ];
    if (canViewEventBilling) base.push({ key: "billing", label: "Billing", icon: Receipt });
    if (canViewEventMenu) base.push({ key: "menu", label: "Menu", icon: ClipboardList });
    if (canViewEventStaffing) base.push({ key: "staffing", label: "Staffing", icon: Users });
    if (canViewEventProcurement) base.push({ key: "procurement", label: "Procurement", icon: Package });
    if (canViewEventReports) base.push({ key: "reports", label: "Reports", icon: BarChart3 });
    return base;
  }, [
    canViewEventBilling,
    canViewEventMenu,
    canViewEventStaffing,
    canViewEventProcurement,
    canViewEventReports,
  ]);

  useEffect(() => {
    if (!rawId) {
      setDetails(null);
      setProcurement({
        headline: null,
        summary: {},
        items: [],
        purchase_orders: [],
        coverage: {},
        notes: {},
      });
      setProcurementLoaded(false);
      setRevenue(null);
      setRevenueLoaded(false);
      setStaffingCosts({
        forecastRows: [],
        actualRows: [],
        costCentres: [],
        forecastTotal: 0,
        actualTotal: 0,
      });
      setStaffingCostsLoaded(false);
      setCommercial(null);
      setCommercialTx([]);
      setCommercialLoaded(false);
      setInitialLoad(false);
      return;
    }

    const seq = ++reqSeq.current;
    const controller = new AbortController();

    if (details?.event) setRefreshing(true);
    else setInitialLoad(true);

    (async () => {
      try {
        const tasks = [
          getEventDetailsWithFallbacks(rawId, { signal: controller.signal }),
          eventIdNum != null
            ? getEventProcurementWithFallbacks(eventIdNum, { signal: controller.signal })
            : Promise.resolve({
                ok: true,
                data: { headline: null, summary: {}, items: [], purchase_orders: [], coverage: {}, notes: {} },
                used: null,
              }),
          eventIdNum != null
            ? getEventRevenueWithFallbacks(eventIdNum, { signal: controller.signal })
            : Promise.resolve({ ok: true, revenue: null, used: null }),
          eventIdNum != null
            ? getEventStaffingCostsWithFallbacks(eventIdNum, { signal: controller.signal })
            : Promise.resolve({
                ok: true,
                data: { forecastRows: [], actualRows: [], costCentres: [], forecastTotal: 0, actualTotal: 0 },
                used: null,
              }),
          eventIdNum != null
            ? getEventCommercialWithFallbacks(eventIdNum, { signal: controller.signal })
            : Promise.resolve({ ok: true, commercial: null, used: null }),
          eventIdNum != null
            ? getEventCommercialTxWithFallbacks(eventIdNum, { signal: controller.signal })
            : Promise.resolve({ ok: true, transactions: [], used: null }),
        ];

        const [d, proc, rev, staffCostResp, comm, tx] = await Promise.all(tasks);

        if (reqSeq.current !== seq) return;

        setDetails(d || null);

        setProcurement(
          proc?.data || {
            headline: null,
            summary: {},
            items: [],
            purchase_orders: [],
            coverage: {},
            notes: {},
          }
        );
        setProcurementLoaded(true);

        setRevenue(rev?.revenue || null);
        setRevenueLoaded(true);

        setStaffingCosts(
          staffCostResp?.data || {
            forecastRows: [],
            actualRows: [],
            costCentres: [],
            forecastTotal: 0,
            actualTotal: 0,
          }
        );
        setStaffingCostsLoaded(true);

        const detailsCommercial = normalizeCommercialPayload(d?.commercial);
        const endpointCommercial = comm?.commercial || null;
        const mergedCommercial = detailsCommercial || endpointCommercial || null;

        setCommercial(mergedCommercial);

        const detailsTx = Array.isArray(detailsCommercial?.transactions) ? detailsCommercial.transactions : [];
        const endpointTx = Array.isArray(tx?.transactions) ? tx.transactions : [];
        setCommercialTx(endpointTx.length > 0 ? endpointTx : detailsTx);
        setCommercialLoaded(true);
      } catch (err) {
        if (controller.signal.aborted) return;
        if (reqSeq.current !== seq) return;

        console.error("Failed to fetch event details/procurement/revenue/commercial/staffing-costs:", err);
        toast.error(err?.message || "Unable to load event details");

        setDetails((prev) => (prev?.event ? prev : null));

        setProcurement((prev) =>
          prev ?? {
            headline: null,
            summary: {},
            items: [],
            purchase_orders: [],
            coverage: {},
            notes: {},
          }
        );
        setProcurementLoaded(true);

        setRevenue((prev) => prev ?? null);
        setRevenueLoaded(true);

        setStaffingCosts((prev) =>
          prev ?? {
            forecastRows: [],
            actualRows: [],
            costCentres: [],
            forecastTotal: 0,
            actualTotal: 0,
          }
        );
        setStaffingCostsLoaded(true);

        setCommercial((prev) => prev ?? null);
        setCommercialTx((prev) => prev ?? []);
        setCommercialLoaded(true);
      } finally {
        if (reqSeq.current !== seq) return;
        setInitialLoad(false);
        setRefreshing(false);
      }
    })();

    return () => controller.abort();
  }, [rawId, retryTick, eventIdNum]); // eslint-disable-line react-hooks/exhaustive-deps

  const menuLines = useMemo(() => normalizeMenuLines(details), [details]);
  const summary = useMemo(() => buildSummaryFromDetails(details, menuLines), [details, menuLines]);

  const staffingForecastFromDetails = toNum(
    details?.staffingForecast?.effective_staffing_cost ??
      details?.staffingForecast?.total ??
      details?.forecast?.staffing_cost_effective ??
      details?.forecast?.staffing_cost,
    summary.staffing_cost || 0
  );

  const staffingForecastTotal = staffingCostsLoaded
    ? Math.max(toNum(staffingCosts?.forecastTotal, 0), staffingForecastFromDetails)
    : staffingForecastFromDetails;

  const staffingActualTotal = staffingCostsLoaded
    ? Math.max(
        toNum(staffingCosts?.actualTotal, 0),
        toNum(details?.staffingForecast?.actual_staffing_cost ?? details?.forecast?.actual_staffing_cost, 0)
      )
    : toNum(details?.staffingForecast?.actual_staffing_cost ?? details?.forecast?.actual_staffing_cost, 0);

  const finance = useMemo(() => {
    const ev = details?.event || {};
    const f = details?.forecast || {};
    const procurementHeadline = procurement?.headline || {};
    const procurementSummary = procurement?.summary || {};
    const txs = Array.isArray(commercialTx) ? commercialTx : [];

    const room_hire = toNum(pickFirst(ev, ["room_hire", "room_hire_fee", "roomHire", "room_cost"], 0), 0);
    const tax_rate = toNum(pickFirst(ev, ["tax_rate", "taxRate"], 0), 0);
    const service_rate = toNum(pickFirst(ev, ["service_rate", "serviceRate"], 0), 0);
    const discount = toNum(pickFirst(ev, ["discount", "discount_amount", "discountAmount"], 0), 0);

    const menuSalesFromLines = menuLines.reduce(
      (acc, l) => acc + toNum(lineUnitPrice(l), 0) * toNum(lineQty(l), 0),
      0
    );

    const menuCostFallbackFromLines = menuLines.reduce(
      (acc, l) => acc + toNum(lineUnitCost(l), 0) * toNum(lineQty(l), 0),
      0
    );

    const quoteBase = menuSalesFromLines + room_hire;

    const taxAmount = toNumNull(f.tax_amount);
    const serviceAmount = toNumNull(f.service_amount);
    const quoteTotalFromServer =
      toNumNull(commercial?.header?.client_total_quote) ??
      toNumNull(commercial?.totals?.quote_total) ??
      toNumNull(f.client_total_quote) ??
      toNumNull(f.client_total_final) ??
      toNumNull(f.client_total);

    const tax = taxAmount != null ? taxAmount : (tax_rate / 100) * quoteBase;
    const service = serviceAmount != null ? serviceAmount : (service_rate / 100) * quoteBase;

    const quoteTotal =
      quoteTotalFromServer != null ? quoteTotalFromServer : quoteBase + tax + service - discount;

    const txSignedAmount = (tx) => {
      const amt = toNum(tx?.amount, 0);
      const dir = String(tx?.direction || "").trim().toLowerCase();
      return dir === "out" ? -amt : amt;
    };

    const txTypeOf = (tx) => String(tx?.tx_type || "").trim().toLowerCase();

    const isQuoteAdjustmentTx = (tx) => {
      const tt = txTypeOf(tx);
      return tt === "charge" || tt === "adjustment";
    };

    const quoteAdjustments = txs.filter(isQuoteAdjustmentTx).reduce((sum, tx) => sum + txSignedAmount(tx), 0);

    const cashTxs = txs.filter((tx) => !isQuoteAdjustmentTx(tx));

    const clientCashIn = cashTxs.reduce((sum, tx) => {
      const signed = txSignedAmount(tx);
      return sum + (signed > 0 ? signed : 0);
    }, 0);

    const clientCashOut = cashTxs.reduce((sum, tx) => {
      const signed = txSignedAmount(tx);
      return sum + (signed < 0 ? Math.abs(signed) : 0);
    }, 0);

    const refundNetSigned = txs
      .filter((tx) => txTypeOf(tx) === "refund")
      .reduce((sum, tx) => sum + txSignedAmount(tx), 0);

    const clientRefunds = Math.abs(Math.min(refundNetSigned, 0));
    const netClientCash = clientCashIn - clientCashOut;
    const quoteAdjustedTotal = quoteTotal + quoteAdjustments;

    const forecastPurchasingCost =
      toNumNull(f.cogs_total) ??
      toNumNull(f.cogs_estimated) ??
      menuCostFallbackFromLines;

    const costOfPurchasing =
      toNumNull(procurementHeadline?.cost_of_purchasing) ??
      toNumNull(procurementSummary?.cost_of_purchasing) ??
      toNumNull(procurementSummary?.total_ordered_cost) ??
      toNumNull(procurementSummary?.total_received_cost) ??
      toNumNull(f.cogs_total ?? f.cogs_estimated) ??
      menuCostFallbackFromLines;

    const receivedPurchasingCost =
      toNumNull(procurementHeadline?.cost_of_purchasing_received) ??
      toNumNull(procurementSummary?.cost_of_purchasing_received) ??
      toNumNull(procurementSummary?.total_received_cost) ??
      toNumNull(procurementSummary?.total_ordered_cost) ??
      toNumNull(f.cogs_total ?? f.cogs_estimated) ??
      menuCostFallbackFromLines;

    const ignoredPurchasingCost = toNumNull(procurementSummary?.total_ignored_cost) ?? 0;

    const outstandingReceiptCost =
      toNumNull(procurementHeadline?.outstanding_receipt_cost) ??
      toNumNull(procurementSummary?.outstanding_receipt_cost) ??
      toNumNull(procurementSummary?.total_open_receipt_cost) ??
      0;

    const purchasingVarianceVsForecast = costOfPurchasing - forecastPurchasingCost;

    const receiptProgressPct =
      toNumNull(procurementHeadline?.receipt_progress_pct) ??
      toNumNull(procurementSummary?.receipt_progress_pct) ??
      (toNum(procurementSummary?.total_ordered_qty, 0) > 0
        ? (toNum(procurementSummary?.total_received_qty, 0) / toNum(procurementSummary?.total_ordered_qty, 0)) * 100
        : 0);

    const operationalCompletionPct =
      toNumNull(procurementHeadline?.operational_completion_pct) ??
      toNumNull(procurementSummary?.operational_completion_pct) ??
      (toNum(procurementSummary?.total_ordered_qty, 0) > 0
        ? ((toNum(procurementSummary?.total_received_qty, 0) + toNum(procurementSummary?.total_ignored_qty, 0)) /
            toNum(procurementSummary?.total_ordered_qty, 0)) *
          100
        : receiptProgressPct);

    const staffingForecast = toNum(staffingForecastTotal, 0);
    const staffingActual = toNum(staffingActualTotal, 0);
    const staffingVariance = staffingActual - staffingForecast;

    const forecastNetProfit = quoteBase - forecastPurchasingCost - staffingForecast;
    const forecastNetMarginPct = quoteBase > 0 ? (forecastNetProfit / quoteBase) * 100 : 0;

    const actualFood = toNumNull(revenue?.actual_food_revenue);
    const actualBev = toNumNull(revenue?.actual_beverage_revenue);
    const actualTotal =
      actualFood === null && actualBev === null ? null : toNum(actualFood, 0) + toNum(actualBev, 0);

    const actualRevenueVariance = actualTotal === null ? null : actualTotal - menuSalesFromLines;

    const actualNetProfitBeforeCommercial =
      actualTotal === null ? null : actualTotal + room_hire - receivedPurchasingCost - staffingActual;

    const actualNetProfit =
      actualTotal === null
        ? null
        : actualTotal + room_hire + quoteAdjustments - receivedPurchasingCost - staffingActual - clientRefunds;

    const profitVariance = actualNetProfit === null ? null : actualNetProfit - forecastNetProfit;

    const actualNetMarginPct =
      actualNetProfit === null ? null : quoteBase > 0 ? (actualNetProfit / quoteBase) * 100 : null;

    const portionsFromLines = menuLines.reduce((a, l) => a + toNum(lineQty(l), 0), 0);
    const guests = toNum(pickFirst(ev, ["expected_guests", "guests", "expectedGuests"], 0), 0);
    const forecastPortions = toNum(details?.forecast?.portions, 0);

    const portionsDisplay =
      portionsFromLines > 0 ? portionsFromLines : forecastPortions > 0 ? forecastPortions : guests;

    const revenuePerPortion = portionsDisplay > 0 ? quoteBase / portionsDisplay : 0;
    const purchasingCostPerPortion = portionsDisplay > 0 ? costOfPurchasing / portionsDisplay : 0;
    const forecastNetPerPortion = portionsDisplay > 0 ? forecastNetProfit / portionsDisplay : 0;
    const actualNetPerPortion = portionsDisplay > 0 && actualNetProfit != null ? actualNetProfit / portionsDisplay : 0;

    return {
      room_hire,
      tax_rate,
      service_rate,
      discount,

      menuSales: menuSalesFromLines,
      quoteBase,
      tax,
      service,
      quoteTotal,
      quoteAdjustedTotal,

      forecastPurchasingCost,
      costOfPurchasing,
      receivedPurchasingCost,
      ignoredPurchasingCost,
      outstandingReceiptCost,
      purchasingVarianceVsForecast,
      receiptProgressPct,
      operationalCompletionPct,

      staffingForecast,
      staffingActual,
      staffingVariance,

      quoteAdjustments,
      clientCashIn,
      clientCashOut,
      netClientCash,
      clientRefunds,

      forecastNetProfit,
      forecastNetMarginPct,

      actualFood,
      actualBev,
      actualTotal,
      actualRevenueVariance,

      actualNetProfitBeforeCommercial,
      actualNetProfit,
      actualNetMarginPct,
      profitVariance,

      portionsDisplay,
      revenuePerPortion,
      purchasingCostPerPortion,
      forecastNetPerPortion,
      actualNetPerPortion,

      menuLinesCount: menuLines.length,
      matchedItemsCount: toNum(procurementSummary.matched_items_count, 0),
      purchasedItemsCount: toNum(procurementSummary.purchased_items_count, 0),
      poCount: toNum(procurementSummary.po_count, 0),
      totalIgnoredQty: toNum(procurementSummary.total_ignored_qty, 0),
      totalRejectedQty: toNum(procurementSummary.total_rejected_qty, 0),
      totalReorderedQty: toNum(procurementSummary.total_reordered_qty, 0),
      totalOpenReceiptQty: toNum(procurementSummary.total_open_receipt_qty, 0),
    };
  }, [
    details,
    menuLines,
    revenue,
    staffingForecastTotal,
    staffingActualTotal,
    procurement,
    commercialTx,
  ]);

  const commercialStats = useMemo(() => {
    const header = commercial?.header || {};
    const totals = commercial?.totals || {};

    const baseQuoteTotal =
      toNumNull(totals?.quote_total) ??
      toNumNull(header?.client_total_quote) ??
      toNumNull(details?.forecast?.client_total_quote) ??
      finance.quoteTotal;

    const depositRequired =
      toNumNull(header?.deposit_required) ??
      toNumNull(pickFirst(details?.event, ["deposit", "deposit_amount", "depositAmount"], null)) ??
      0;

    const txs = Array.isArray(commercialTx) ? commercialTx : [];

    const netAmount = (tx) => {
      const amt = toNum(tx?.amount, 0);
      const dir = String(tx?.direction || "").toLowerCase();
      return dir === "out" ? -amt : amt;
    };

    const isQuoteAdjustment = (t) => {
      const tt = String(t?.tx_type || "").toLowerCase();
      return tt === "charge" || tt === "adjustment";
    };

    const quoteAdjustments = txs.filter(isQuoteAdjustment).reduce((s, t) => s + netAmount(t), 0);
    const effectiveQuoteTotal = toNum(baseQuoteTotal, 0) + toNum(quoteAdjustments, 0);

    const cashTxs = txs.filter((t) => !isQuoteAdjustment(t));
    const cashReceivedNet = toNumNull(totals?.net_received) ?? cashTxs.reduce((s, t) => s + netAmount(t), 0);

    const depositPaid =
      toNumNull(totals?.deposit_received) ??
      cashTxs
        .filter((t) => String(t?.tx_type || "").toLowerCase() === "deposit")
        .reduce((s, t) => s + netAmount(t), 0);

    const paymentsPaid =
      toNumNull(totals?.amount_paid) ??
      cashTxs
        .filter((t) => String(t?.tx_type || "").toLowerCase() === "payment")
        .reduce((s, t) => s + netAmount(t), 0);

    const refundsNet =
      toNumNull(totals?.refunds_net) ??
      cashTxs
        .filter((t) => String(t?.tx_type || "").toLowerCase() === "refund")
        .reduce((s, t) => s + netAmount(t), 0);

    const balance = toNumNull(totals?.balance_due) ?? (effectiveQuoteTotal - cashReceivedNet);

    const eps = 0.01;
    let status =
      safeStr(totals?.payment_status, "") !== "—"
        ? safeStr(totals?.payment_status)
        : safeStr(header?.quote_status, "—");

    if (effectiveQuoteTotal > 0 && cashReceivedNet >= effectiveQuoteTotal - eps) status = "Paid (Full)";
    else if (depositRequired > 0 && depositPaid >= depositRequired - eps) status = "Deposit Paid";

    const quoteSentAt = header?.quote_sent_at || null;

    return {
      baseQuoteTotal: toNum(baseQuoteTotal, 0),
      quoteAdjustments: toNum(quoteAdjustments, 0),
      effectiveQuoteTotal: toNum(effectiveQuoteTotal, 0),

      depositRequired: toNum(depositRequired, 0),
      depositPaid: toNum(depositPaid, 0),

      paymentsPaid: toNum(paymentsPaid, 0),
      refundsNet: toNum(refundsNet, 0),

      cashReceivedNet: toNum(cashReceivedNet, 0),
      balance: toNum(balance, 0),

      status,
      quoteSentAt,
    };
  }, [commercial, commercialTx, details, finance.quoteTotal]);

  const financeHeadlineCards = useMemo(() => {
    return [
      {
        title: "Quote Total",
        value: gbp(finance.quoteAdjustedTotal),
        tone: "text-slate-950",
        subtitle: "Commercial quote including adjustments",
        bg: "bg-white border-slate-200",
      },
      {
        title: "Cost of Purchasing",
        value: gbp(finance.costOfPurchasing),
        tone: "text-slate-950",
        subtitle: "Canonical procurement-driven cost",
        bg: "bg-white border-slate-200",
      },
      {
        title: "Actual Revenue",
        value: finance.actualTotal == null ? "—" : gbp(finance.actualTotal),
        tone: valueToneClass(finance.actualTotal),
        subtitle: "Submitted food + beverage revenue",
        bg: "bg-white border-slate-200",
      },
      {
        title: "Actual Net Profit",
        value: finance.actualNetProfit == null ? "—" : gbp(finance.actualNetProfit),
        tone: valueToneClass(finance.actualNetProfit),
        subtitle: "After purchasing, staffing, adjustments, and refunds",
        bg: varianceBgClass(finance.actualNetProfit),
      },
      {
        title: "Balance Due",
        value: gbp(commercialStats.balance),
        tone: commercialStats.balance > 0 ? "text-amber-700" : "text-emerald-700",
        subtitle: "Remaining commercial balance",
        bg: "bg-white border-slate-200",
      },
      {
        title: "Operational Completion",
        value: pct(finance.operationalCompletionPct),
        tone: "text-slate-950",
        subtitle: "Received + ignored against ordered quantity",
        bg: "bg-white border-slate-200",
      },
    ];
  }, [finance, commercialStats]);

  if (initialLoad) {
    return (
      <div className="min-h-screen bg-slate-50 px-6 py-8 md:px-10">
        <div className="mx-auto max-w-7xl">
          <div className="rounded-3xl border border-slate-200 bg-white p-6 text-slate-700 shadow-sm">
            Loading event details…
          </div>
        </div>
      </div>
    );
  }

  if (!details?.event) {
    return (
      <div className="min-h-screen bg-slate-50 px-6 py-8 md:px-10">
        <div className="mx-auto max-w-7xl">
          <div className="rounded-3xl border border-rose-200 bg-rose-50 p-6 shadow-sm">
            <div className="font-semibold text-rose-700">Event not found</div>
            <div className="mt-1 text-sm text-rose-600">
              The details endpoint returned no event payload.
            </div>
            <button
              onClick={() => setRetryTick((x) => x + 1)}
              className="mt-3 inline-flex items-center gap-2 rounded-xl bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700"
              type="button"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const ev = details.event;

  const dateStr = asDateGB(pickFirst(ev, ["event_date", "date", "starts_at", "startsAt"], null));
  const guests = toNum(pickFirst(ev, ["expected_guests", "guests", "expectedGuests"], 0), 0);

  const venue = pickFirst(ev, ["venue_name", "venue", "venueName"], "");
  const kickoff = pickFirst(ev, ["kickoff_time", "start_time", "startTime"], "");
  const planner =
    pickFirst(ev, ["planner_name", "planner", "plannerName"], "") ||
    pickFirst(ev?.planner, ["planner_name", "full_name", "email", "name"], "");

  const notes =
    pickFirst(ev, ["notes", "event_notes", "event_note", "description"], "") ||
    pickFirst(details, ["notes"], "");

  const roomHireRaw = pickFirst(ev, ["room_hire", "room_hire_fee", "roomHire", "room_cost"], null);
  const clientName =
    pickFirst(details?.client, ["client_name", "name"], "") ||
    pickFirst(ev, ["client_name", "client", "clientName"], "");

  const pdfNameBase =
    (ev?.event_name ? String(ev.event_name).replace(/[^\w\s-]/g, "").trim().replace(/\s+/g, "-") : "") ||
    `event-${rawId}`;

  const onDownloadPdf = async () => {
    if (eventIdNum == null) {
      toast.error("Invalid event id");
      return;
    }
    try {
      setPdfBusy(true);
      const out = await downloadEventPdfWithFallbacks(eventIdNum, pdfNameBase);
      toast.success(`PDF opened/downloaded (${out.used})`);
    } catch (e) {
      console.error("PDF download failed:", e);
      toast.error(e?.message || "Failed to download PDF");
    } finally {
      setPdfBusy(false);
    }
  };

  const onDownloadExcel = async () => {
    if (eventIdNum == null) {
      toast.error("Invalid event id");
      return;
    }
    try {
      setExcelBusy(true);
      const out = await downloadEventExcelWithFallbacks(eventIdNum, pdfNameBase);
      toast.success(`Excel downloaded (${out.used})`);
    } catch (e) {
      console.error("Excel download failed:", e);
      toast.error(e?.message || "Failed to download Excel");
    } finally {
      setExcelBusy(false);
    }
  };

  const openEditEvent = () => {
    if (eventIdNum == null) return;
    navigate(`/events/${eventIdNum}/edit`);
  };

  const openMenu = () => {
    if (eventIdNum == null) return;
    navigate(`/events/${eventIdNum}/menu`);
  };

  const openStockCheck = () => {
    if (eventIdNum == null) return;
    navigate(`/events/${eventIdNum}/menu?action=stockcheck`);
  };

  const openReportsDashboard = () => {
    if (eventIdNum == null) return;
    navigate(`/events/reports?eventId=${eventIdNum}`);
  };

  const reportSections =
    details?.reportSections ||
    details?.report_sections ||
    details?.manager_report_sections ||
    details?.managerReportSections ||
    [];

  const handleTxAddedLocal = (newTx) => {
    if (!newTx) return;
    setCommercialTx((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      const id = newTx?.id;
      if (id == null) return [newTx, ...arr];
      const filtered = arr.filter((t) => t?.id !== id);
      return [newTx, ...filtered];
    });
  };

  const readinessItems = [
    { label: "Menu", ready: finance.menuLinesCount > 0 },
    { label: "Procurement", ready: finance.poCount > 0 },
    { label: "Revenue Actuals", ready: finance.actualTotal != null },
    { label: "Staffing Actuals", ready: staffingActualTotal > 0 },
  ];

  const readyCount = readinessItems.filter((x) => x.ready).length;
  const readinessPercent = readinessItems.length > 0 ? (readyCount / readinessItems.length) * 100 : 0;

  return (
    <div className="min-h-screen bg-slate-50 px-6 py-8 text-slate-900 md:px-10">
      <div className="mx-auto max-w-7xl space-y-5">
        {refreshing ? (
          <div className="rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-700">
            Refreshing event details…
          </div>
        ) : null}

        <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm md:p-8">
          <div className="flex flex-col gap-6 xl:flex-row xl:items-start xl:justify-between">
            <div className="min-w-0 flex-1">
              <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700">
                <Activity className="h-3.5 w-3.5" />
                Event Overview
              </div>

              <h1 className="mt-4 text-3xl font-bold tracking-tight text-slate-950 md:text-5xl">
                {safeStr(ev.event_name, `Event #${rawId}`)}
              </h1>

              <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600 md:text-base">
                A cleaner operational view of event performance, planning status, finance, procurement, staffing, and
                commercial progress.
              </p>

              <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <HeaderMeta icon={CalendarDays} label="Event Date" value={dateStr} />
                <HeaderMeta icon={Clock3} label="Kickoff" value={safeStr(kickoff)} />
                <HeaderMeta icon={User2} label="Planner" value={safeStr(planner)} />
                <HeaderMeta icon={MapPin} label="Venue" value={safeStr(venue)} />
              </div>
            </div>

            <div className="w-full xl:max-w-sm">
              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">Readiness</div>
                    <div className="mt-1 text-xs text-slate-500">Operational signals for this event.</div>
                  </div>
                  <div className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700">
                    {Math.round(readinessPercent)}%
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-2">
                  {readinessItems.map((item) => (
                    <ReadinessBadge key={item.label} label={item.label} ready={item.ready} />
                  ))}
                </div>

                <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-500">Primary Client</div>
                  <div className="mt-1 text-lg font-semibold text-slate-950">{safeStr(clientName)}</div>
                  <div className="mt-3 flex items-center justify-between text-sm">
                    <span className="text-slate-500">Quote Total</span>
                    <span className="font-semibold text-slate-950">{gbp(finance.quoteAdjustedTotal)}</span>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-sm">
                    <span className="text-slate-500">Balance Due</span>
                    <span className="font-semibold text-slate-950">{gbp(commercialStats.balance)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2">
              <ToolbarButton onClick={openEditEvent} icon={Pencil} label="Edit Event" />
              <ToolbarButton onClick={openMenu} icon={UtensilsCrossed} label="Menu" />
              <ToolbarButton onClick={openStockCheck} icon={PackageSearch} label="Stock Check" />
            </div>

            <div className="flex flex-wrap gap-2">
              <SecondaryButton
                onClick={onDownloadPdf}
                disabled={pdfBusy}
                icon={FileDown}
                label={pdfBusy ? "Preparing PDF..." : "PDF"}
              />
              <SecondaryButton
                onClick={onDownloadExcel}
                disabled={excelBusy}
                icon={Sheet}
                label={excelBusy ? "Preparing Excel..." : "Excel"}
              />
              <SecondaryButton onClick={() => setRetryTick((x) => x + 1)} icon={RefreshCw} label="Refresh" />
            </div>
          </div>
        </section>

        <section className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap gap-2">
            {tabs.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={
                    active
                      ? "inline-flex items-center gap-2 rounded-2xl border border-slate-900 bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white"
                      : "inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-white"
                  }
                  type="button"
                >
                  <Icon className="h-4 w-4" />
                  {t.label}
                </button>
              );
            })}
          </div>
        </section>

        {tab === "overview" ? (
          <div className="space-y-5">
            <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
              <SectionHeader
                title="Executive Summary"
                subtitle="The top-level picture of this event without overloading the screen."
              />

              <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {financeHeadlineCards.map((card) => (
                  <ExecutiveMetricCard
                    key={card.title}
                    title={card.title}
                    value={card.value}
                    subtitle={card.subtitle}
                    toneClass={card.tone}
                    bgClass={card.bg}
                  />
                ))}
              </div>
            </section>

            <section className="grid grid-cols-1 gap-5 xl:grid-cols-[0.95fr_1.05fr]">
              <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
                <SectionHeader
                  title="Event Snapshot"
                  subtitle="Identity, logistics, and saved commercial context."
                />
                <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <DetailStat label="Client" value={safeStr(clientName)} />
                  <DetailStat label="Venue" value={safeStr(venue)} />
                  <DetailStat label="Kickoff Time" value={safeStr(kickoff)} />
                  <DetailStat label="Guests" value={guests || "—"} />
                  <DetailStat label="Portions" value={finance.portionsDisplay || "—"} />
                  <DetailStat label="Menu Lines" value={finance.menuLinesCount ?? 0} />
                  <DetailStat label="Room Hire (Saved)" value={roomHireRaw != null ? gbp(roomHireRaw) : "—"} />
                  <DetailStat label="Receipt Progress" value={pct(finance.receiptProgressPct)} />
                </div>
              </div>

              <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
                <SectionHeader
                  title="Event Notes"
                  subtitle="Operational notes and saved commentary."
                />
                <div className="mt-5 min-h-[280px] whitespace-pre-wrap rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm leading-6 text-slate-700">
                  {notes || "No notes saved for this event yet."}
                </div>
              </div>
            </section>

            <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
              <SectionHeader
                title="Financial Performance"
                subtitle="A cleaner grouped view of revenue, cost, profit, and commercial status."
              />

              <div className="mt-6 grid grid-cols-1 gap-5 xl:grid-cols-2">
                <DataPanel
                  title="Revenue & Quote"
                  icon={Banknote}
                  items={[
                    ["Menu Sales", gbp(finance.menuSales)],
                    ["Room Hire", gbp(finance.room_hire)],
                    ["Quote Total", gbp(finance.quoteTotal)],
                    ["Quote Adjustments", gbp(finance.quoteAdjustments)],
                    ["Adjusted Quote", gbp(finance.quoteAdjustedTotal)],
                    ["Actual Food Revenue", finance.actualFood == null ? "—" : gbp(finance.actualFood)],
                    ["Actual Beverage Revenue", finance.actualBev == null ? "—" : gbp(finance.actualBev)],
                    ["Actual Total Revenue", finance.actualTotal == null ? "—" : gbp(finance.actualTotal)],
                    ["Revenue Variance", finance.actualRevenueVariance == null ? "—" : gbp(finance.actualRevenueVariance)],
                  ]}
                />

                <DataPanel
                  title="Costs & Operations"
                  icon={ClipboardCheck}
                  items={[
                    ["Cost of Purchasing", gbp(finance.costOfPurchasing)],
                    ["Received Purchasing Cost", gbp(finance.receivedPurchasingCost)],
                    ["Forecast Purchasing Cost", gbp(finance.forecastPurchasingCost)],
                    ["Purchasing Variance", gbp(finance.purchasingVarianceVsForecast)],
                    ["Outstanding Receipt Cost", gbp(finance.outstandingReceiptCost)],
                    ["Ignored Procurement Cost", gbp(finance.ignoredPurchasingCost)],
                    ["Forecast Staffing", gbp(finance.staffingForecast)],
                    ["Actual Staffing", gbp(finance.staffingActual)],
                    ["Staffing Variance", gbp(finance.staffingVariance)],
                  ]}
                />

                <DataPanel
                  title="Profit & Margin"
                  icon={TrendingUp}
                  items={[
                    ["Forecast Net Profit", gbp(finance.forecastNetProfit)],
                    ["Forecast Net Margin", pct(finance.forecastNetMarginPct)],
                    ["Actual Net Profit", finance.actualNetProfit == null ? "—" : gbp(finance.actualNetProfit)],
                    ["Current Net Margin", finance.actualNetMarginPct == null ? "—" : pct(finance.actualNetMarginPct)],
                    ["Profit Variance", finance.profitVariance == null ? "—" : gbp(finance.profitVariance)],
                    ["Revenue per Portion", gbp(finance.revenuePerPortion)],
                    ["Purchasing Cost per Portion", gbp(finance.purchasingCostPerPortion)],
                    [
                      "Net Profit per Portion",
                      finance.actualNetProfit == null
                        ? gbp(finance.forecastNetPerPortion)
                        : gbp(finance.actualNetPerPortion),
                    ],
                    ["Client Refunds", gbp(finance.clientRefunds)],
                  ]}
                />

                <DataPanel
                  title="Commercial & Procurement Snapshot"
                  icon={Wallet}
                  items={[
                    ["Commercial Status", safeStr(commercialStats.status)],
                    ["Balance Due", gbp(commercialStats.balance)],
                    ["Deposit Required", gbp(commercialStats.depositRequired)],
                    ["Deposit Paid", gbp(commercialStats.depositPaid)],
                    ["Net Client Cash", gbp(finance.netClientCash)],
                    ["PO Count", finance.poCount],
                    ["Matched Items", finance.matchedItemsCount],
                    ["Purchased Items", finance.purchasedItemsCount],
                    ["Operational Completion", pct(finance.operationalCompletionPct)],
                  ]}
                />
              </div>

              <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                <div className="font-semibold text-slate-900">Reading guide</div>
                <div className="mt-2 grid gap-1">
                  <div>• Cost of Purchasing is sourced from procurement headline first, then summary.</div>
                  <div>• Operational Completion includes received and ignored quantities.</div>
                  <div>• Quote Adjustments change what the client owes; payments/refunds move cash.</div>
                  <div>• Actual Net Profit includes actual revenue, room hire, adjustments, staffing, purchasing, and refunds.</div>
                </div>
              </div>
            </section>
          </div>
        ) : null}

        {tab === "manager" ? (
          <div className="space-y-5">
            <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
              <SectionHeader
                title="Manager Workspace"
                subtitle="Focused actions for commercial updates, actual revenue, and staffing actuals."
              />
              <div className="mt-3 text-sm text-slate-600">
                This workspace is intentionally calmer: update the event’s commercial record, record revenue, and post
                actual staffing costs without the dashboard overload.
              </div>
            </section>

            {eventIdNum != null ? (
              <>
                <ManagerComparisonPanel
                  finance={finance}
                  staffingCosts={staffingCosts}
                  staffingCostsLoaded={staffingCostsLoaded}
                />

                <CommercialPanel
                  eventId={eventIdNum}
                  commercial={commercial}
                  transactions={commercialTx}
                  forecastQuoteTotal={finance.quoteTotal}
                  stats={commercialStats}
                  onTxAdded={handleTxAddedLocal}
                  onChanged={async () => {
                    try {
                      const [c, t, d] = await Promise.all([
                        getEventCommercialWithFallbacks(eventIdNum),
                        getEventCommercialTxWithFallbacks(eventIdNum),
                        getEventDetailsWithFallbacks(eventIdNum),
                      ]);

                      const detailsCommercial = normalizeCommercialPayload(d?.commercial);
                      const endpointCommercial = c?.commercial || null;
                      const mergedCommercial = detailsCommercial || endpointCommercial || null;

                      setCommercial(mergedCommercial);

                      const detailsTx = Array.isArray(detailsCommercial?.transactions)
                        ? detailsCommercial.transactions
                        : [];
                      const endpointTx = Array.isArray(t?.transactions) ? t.transactions : [];
                      setCommercialTx(endpointTx.length > 0 ? endpointTx : detailsTx);
                      setCommercialLoaded(true);
                    } catch {
                      // keep UI stable
                    }
                  }}
                />

                <RevenueForm
                  eventId={eventIdNum}
                  currentFood={revenue?.actual_food_revenue}
                  currentBeverage={revenue?.actual_beverage_revenue}
                  onSaved={async () => {
                    try {
                      const rev = await getEventRevenueWithFallbacks(eventIdNum);
                      setRevenue(rev?.revenue || null);
                      setRevenueLoaded(true);
                    } catch {
                      // ignore
                    }
                    setRetryTick((x) => x + 1);
                  }}
                />

                <StaffingCostManagerPanel
                  eventId={eventIdNum}
                  staffingCosts={staffingCosts}
                  loaded={staffingCostsLoaded}
                  forecastTotal={finance.staffingForecast}
                  onSaved={async () => {
                    try {
                      const out = await getEventStaffingCostsWithFallbacks(eventIdNum);
                      setStaffingCosts(
                        out?.data || {
                          forecastRows: [],
                          actualRows: [],
                          costCentres: [],
                          forecastTotal: 0,
                          actualTotal: 0,
                        }
                      );
                      setStaffingCostsLoaded(true);
                    } catch {
                      // ignore
                    }
                    setRetryTick((x) => x + 1);
                  }}
                />
              </>
            ) : (
              <div className="rounded-2xl border border-slate-200 bg-white p-6 text-slate-700 shadow-sm">
                Invalid event id.
              </div>
            )}
          </div>
        ) : null}

        {tab === "billing" && canViewEventBilling
          ? eventIdNum != null
            ? (
              <SectionErrorBoundary title="Billing" onRetry={() => setRetryTick((x) => x + 1)}>
                <section className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm">
                  <SectionHeader
                    title="Billing"
                    subtitle="Client invoices, payments, balances, and commercial-linked billing flows."
                  />
                  <div className="mt-4">
                    <EventBillingPanel key={`billing-${eventIdNum}`} eventId={eventIdNum} />
                  </div>
                </section>
              </SectionErrorBoundary>
            )
            : null
          : null}

        {tab === "menu" && canViewEventMenu
          ? eventIdNum != null
            ? (
              <SectionErrorBoundary title="Menu" onRetry={() => setRetryTick((x) => x + 1)}>
                <section className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm">
                  <SectionHeader title="Menu" subtitle="Detailed menu setup for this event." />
                  <div className="mt-4">
                    <EventMenuOverview key={`menu-${eventIdNum}`} eventId={eventIdNum} user={user} />
                  </div>
                </section>
              </SectionErrorBoundary>
            )
            : null
          : null}

        {tab === "staffing" && canViewEventStaffing
          ? eventIdNum != null
            ? (
              <SectionErrorBoundary title="Staffing" onRetry={() => setRetryTick((x) => x + 1)}>
                <section className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm">
                  <SectionHeader title="Staffing" subtitle="Staff planning and workforce detail for this event." />
                  <div className="mt-4">
                    <EventStaffingOverview key={`staff-${eventIdNum}`} eventId={eventIdNum} />
                  </div>
                </section>
              </SectionErrorBoundary>
            )
            : null
          : null}

        {tab === "procurement" && canViewEventProcurement
          ? eventIdNum != null
            ? (
              <SectionErrorBoundary title="Procurement" onRetry={() => setRetryTick((x) => x + 1)}>
                <section className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm">
                  <SectionHeader title="Procurement" subtitle="Full purchasing, receipt, and PO-linked intelligence." />
                  <div className="mt-4">
                    <EventProcurementOverview key={`proc-${eventIdNum}`} eventId={eventIdNum} />
                  </div>
                </section>
              </SectionErrorBoundary>
            )
            : null
          : null}

        {tab === "reports" && canViewEventReports ? (
          <SectionErrorBoundary title="Reports" onRetry={() => setRetryTick((x) => x + 1)}>
            <section className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm">
              <SectionHeader title="Reports" subtitle="Manager reporting sections and exported event intelligence." />

              <div className="mt-4 flex flex-wrap gap-2">
                <ToolbarButton onClick={openReportsDashboard} icon={BarChart3} label="Open Reports Dashboard" />
                <SecondaryButton
                  onClick={onDownloadPdf}
                  disabled={pdfBusy}
                  icon={FileDown}
                  label={pdfBusy ? "Preparing PDF..." : "Download PDF"}
                />
                <SecondaryButton
                  onClick={onDownloadExcel}
                  disabled={excelBusy}
                  icon={Sheet}
                  label={excelBusy ? "Preparing Excel..." : "Download Excel"}
                />
              </div>

              <div className="mt-4">
                <EventManagerReportViewer reportSections={Array.isArray(reportSections) ? reportSections : []} />
              </div>
            </section>
          </SectionErrorBoundary>
        ) : null}
      </div>
    </div>
  );
}

/* ───────────────── UI pieces ───────────────── */
function SectionHeader({ title, subtitle }) {
  return (
    <div className="flex flex-col gap-1 md:flex-row md:items-end md:justify-between">
      <div>
        <h2 className="text-xl font-bold text-slate-950">{title}</h2>
        <p className="mt-1 text-sm text-slate-600">{subtitle}</p>
      </div>
    </div>
  );
}

function HeaderMeta({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className="mt-2 text-sm font-semibold text-slate-900">{value}</div>
    </div>
  );
}

function ReadinessBadge({ label, ready }) {
  return (
    <div
      className={
        ready
          ? "rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-3"
          : "rounded-2xl border border-amber-200 bg-amber-50 px-3 py-3"
      }
    >
      <div className="flex items-center gap-2 text-sm font-semibold">
        {ready ? (
          <CheckCircle2 className="h-4 w-4 text-emerald-700" />
        ) : (
          <AlertTriangle className="h-4 w-4 text-amber-700" />
        )}
        <span className={ready ? "text-emerald-800" : "text-amber-800"}>{label}</span>
      </div>
    </div>
  );
}

function ToolbarButton({ onClick, icon: Icon, label }) {
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-white"
      type="button"
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}

function SecondaryButton({ onClick, disabled = false, icon: Icon, label }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
      type="button"
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}

const ExecutiveMetricCard = ({ title, value, subtitle, toneClass = "text-slate-900", bgClass = "bg-white" }) => (
  <div className={`rounded-3xl border p-5 ${bgClass}`}>
    <div className="text-sm font-medium text-slate-600">{title}</div>
    <div className={`mt-3 text-3xl font-bold md:text-4xl ${toneClass}`}>{value}</div>
    <div className="mt-2 text-xs leading-relaxed text-slate-500">{subtitle}</div>
  </div>
);

const DetailStat = ({ label, value }) => (
  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
    <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
    <div className="mt-1 text-sm font-semibold text-slate-950">{value}</div>
  </div>
);

function DataPanel({ title, icon: Icon, items }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
      <div className="mb-4 flex items-center gap-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-2.5">
          <Icon className="h-4 w-4 text-slate-700" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-slate-950">{title}</h3>
        </div>
      </div>

      <div className="space-y-2">
        {items.map(([label, value]) => (
          <div
            key={label}
            className="flex items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white px-4 py-3"
          >
            <div className="text-sm text-slate-600">{label}</div>
            <div className="text-sm font-semibold text-slate-950">{value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const SummaryCardSmall = ({ title, value }) => (
  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
    <div className="text-xs text-slate-500">{title}</div>
    <div className="mt-0.5 text-lg font-semibold text-slate-950">{value}</div>
  </div>
);

function ComparisonStat({ title, forecast, actual, variance }) {
  const varianceNum = toNumNull(variance);
  const varianceTone =
    varianceNum == null
      ? "text-slate-700"
      : varianceNum > 0
        ? "text-amber-700"
        : varianceNum < 0
          ? "text-emerald-700"
          : "text-slate-700";

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <div className="text-xs uppercase tracking-wide text-slate-500">{title}</div>
      <div className="mt-3 grid grid-cols-3 gap-2 text-sm">
        <div>
          <div className="text-[11px] text-slate-500">Forecast</div>
          <div className="font-semibold text-slate-950">{forecast}</div>
        </div>
        <div>
          <div className="text-[11px] text-slate-500">Actual</div>
          <div className="font-semibold text-slate-950">{actual}</div>
        </div>
        <div>
          <div className="text-[11px] text-slate-500">Variance</div>
          <div className={`font-semibold ${varianceTone}`}>{variance}</div>
        </div>
      </div>
    </div>
  );
}

function ManagerComparisonPanel({ finance, staffingCosts, staffingCostsLoaded }) {
  const forecastRevenue = toNum(finance.menuSales, 0);
  const actualRevenue = finance.actualTotal == null ? null : toNum(finance.actualTotal, 0);
  const revenueVariance = actualRevenue == null ? null : actualRevenue - forecastRevenue;

  const forecastStaffing = toNum(finance.staffingForecast, 0);
  const actualStaffing = toNum(finance.staffingActual, 0);
  const staffingVariance = actualStaffing - forecastStaffing;

  const forecastNet = toNum(finance.forecastNetProfit, 0);
  const actualNet = finance.actualNetProfit == null ? null : toNum(finance.actualNetProfit, 0);
  const netVariance = actualNet == null ? null : actualNet - forecastNet;

  return (
    <div className="space-y-4 rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
      <div>
        <h2 className="text-xl font-bold text-slate-950">Management Comparison</h2>
        <div className="mt-1 text-sm text-slate-500">
          Forecast vs actual using revenue, purchasing cost, and staffing.
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <ComparisonStat
          title="Revenue"
          forecast={gbp(forecastRevenue)}
          actual={actualRevenue == null ? "—" : gbp(actualRevenue)}
          variance={revenueVariance == null ? "—" : gbp(revenueVariance)}
        />

        <ComparisonStat
          title="Staffing Cost"
          forecast={gbp(forecastStaffing)}
          actual={staffingCostsLoaded ? gbp(actualStaffing) : "—"}
          variance={staffingCostsLoaded ? gbp(staffingVariance) : "—"}
        />

        <ComparisonStat
          title="Net Profit"
          forecast={gbp(forecastNet)}
          actual={actualNet == null ? "—" : gbp(actualNet)}
          variance={netVariance == null ? "—" : gbp(netVariance)}
        />
      </div>

      {staffingCostsLoaded && Array.isArray(staffingCosts?.actualRows) && staffingCosts.actualRows.length > 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <div className="mb-2 text-sm font-semibold text-slate-950">Actual Staffing by Cost Centre</div>
          <div className="overflow-auto rounded-xl border border-slate-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-700">
                <tr>
                  <th className="px-3 py-2 text-left">Cost Centre</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2 text-left">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {staffingCosts.actualRows.map((row, idx) => {
                  const cc =
                    (staffingCosts.costCentres || []).find(
                      (c) => Number(c.cost_centre_id) === Number(row.cost_centre_id)
                    ) || null;
                  return (
                    <tr key={`${row.cost_centre_id}-${idx}`} className="text-slate-800">
                      <td className="px-3 py-2">
                        {cc
                          ? `${cc.code ? `${cc.code} · ` : ""}${cc.name}`
                          : row.cost_centre_name || `Cost Centre ${row.cost_centre_id}`}
                      </td>
                      <td className="px-3 py-2 text-right">{gbp(row.amount)}</td>
                      <td className="px-3 py-2 text-slate-600">{safeStr(row.notes, "—")}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function CommercialPanel({ eventId, commercial, transactions, forecastQuoteTotal, stats, onChanged, onTxAdded }) {
  const header = commercial?.header || {};

  const [quoteStatus, setQuoteStatus] = useState(header?.quote_status ?? "");
  const [quoteSentAt, setQuoteSentAt] = useState(asDateTimeLocalValue(header?.quote_sent_at));
  const [clientTotalQuote, setClientTotalQuote] = useState(
    header?.client_total_quote != null ? String(header.client_total_quote) : String(toNum(forecastQuoteTotal, 0))
  );
  const [depositRequired, setDepositRequired] = useState(
    header?.deposit_required != null ? String(header.deposit_required) : ""
  );
  const [notes, setNotes] = useState(header?.notes ?? "");
  const [saving, setSaving] = useState(false);

  const [txType, setTxType] = useState("deposit");
  const [txDir, setTxDir] = useState("in");
  const [txAmount, setTxAmount] = useState("");
  const [txMethod, setTxMethod] = useState("");
  const [txRef, setTxRef] = useState("");
  const [txNotes, setTxNotes] = useState("");
  const [txSaving, setTxSaving] = useState(false);

  useEffect(() => {
    const nextHeader = commercial?.header || {};
    setQuoteStatus(nextHeader?.quote_status ?? "");
    setQuoteSentAt(asDateTimeLocalValue(nextHeader?.quote_sent_at));
    setClientTotalQuote(
      nextHeader?.client_total_quote != null
        ? String(nextHeader.client_total_quote)
        : String(toNum(forecastQuoteTotal, 0))
    );
    setDepositRequired(nextHeader?.deposit_required != null ? String(nextHeader.deposit_required) : "");
    setNotes(nextHeader?.notes ?? "");
  }, [
    commercial?.header?.event_id,
    commercial?.header?.quote_status,
    commercial?.header?.quote_sent_at,
    commercial?.header?.client_total_quote,
    commercial?.header?.deposit_required,
    commercial?.header?.notes,
    forecastQuoteTotal,
  ]);

  const txList = Array.isArray(transactions) ? transactions : [];

  const saveHeader = async () => {
    setSaving(true);
    try {
      const payload = {
        quote_status: quoteStatus || null,
        quote_sent_at: dateTimeLocalToIso(quoteSentAt),
        client_total_quote: clientTotalQuote === "" ? null : Number(clientTotalQuote),
        deposit_required: depositRequired === "" ? null : Number(depositRequired),
        notes: notes || null,
      };

      if (
        (payload.client_total_quote != null && !Number.isFinite(payload.client_total_quote)) ||
        (payload.deposit_required != null && !Number.isFinite(payload.deposit_required))
      ) {
        toast.error("Commercial values must be valid numbers.");
        return;
      }

      await putEventCommercialWithFallbacks(eventId, payload);
      toast.success("Commercial header saved");
      if (onChanged) await onChanged();
    } catch (e) {
      console.error("Save commercial failed:", e);
      toast.error(e?.response?.data?.error || e?.message || "Failed to save commercial header");
    } finally {
      setSaving(false);
    }
  };

  const addTx = async () => {
    if (txAmount === "") {
      toast.error("Enter a transaction amount.");
      return;
    }
    const amt = Number(txAmount);
    if (!Number.isFinite(amt) || amt < 0) {
      toast.error("Transaction amount must be a valid non-negative number.");
      return;
    }

    setTxSaving(true);
    try {
      const payload = {
        tx_type: txType,
        direction: txDir,
        amount: amt,
        method: txMethod || null,
        reference: txRef || null,
        notes: txNotes || null,
      };

      const out = await postEventCommercialTxWithFallbacks(eventId, payload);

      if (out?.transaction && onTxAdded) onTxAdded(out.transaction);
      else if (out?.transactions?.[0] && onTxAdded) onTxAdded(out.transactions[0]);

      toast.success("Transaction added");

      setTxAmount("");
      setTxMethod("");
      setTxRef("");
      setTxNotes("");

      if (onChanged) await onChanged();
    } catch (e) {
      console.error("Add tx failed:", e);
      toast.error(e?.response?.data?.error || e?.message || "Failed to add transaction");
    } finally {
      setTxSaving(false);
    }
  };

  return (
    <div className="space-y-4 rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-950">Commercial Tracking</h2>
          <div className="mt-1 text-sm text-slate-500">
            Track quote status, deposits, payments, refunds, and quote adjustments.
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-slate-500">Status</div>
          <div className="text-sm font-semibold text-slate-950">{safeStr(stats?.status)}</div>
          <div className="mt-1 text-[11px] text-slate-500">Balance: {gbp(stats?.balance ?? 0)}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">
        <SummaryCardSmall title="Quote (Base)" value={gbp(stats?.baseQuoteTotal ?? 0)} />
        <SummaryCardSmall title="Quote Adj (+/-)" value={gbp(stats?.quoteAdjustments ?? 0)} />
        <SummaryCardSmall title="Quote (Effective)" value={gbp(stats?.effectiveQuoteTotal ?? 0)} />
        <SummaryCardSmall title="Cash Received (Net)" value={gbp(stats?.cashReceivedNet ?? 0)} />
        <SummaryCardSmall title="Deposit Paid" value={gbp(stats?.depositPaid ?? 0)} />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm text-slate-600">Quote Status</label>
          <input
            value={quoteStatus}
            onChange={(e) => setQuoteStatus(e.target.value)}
            placeholder="e.g. Draft / Quote Sent / Deposit Paid / Paid"
            className="w-full rounded-2xl border border-slate-300 bg-slate-50 p-2.5 text-slate-950"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm text-slate-600">Quote Sent At</label>
          <input
            type="datetime-local"
            value={quoteSentAt}
            onChange={(e) => setQuoteSentAt(e.target.value)}
            className="w-full rounded-2xl border border-slate-300 bg-slate-50 p-2.5 text-slate-950"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm text-slate-600">Client Total Quote (£)</label>
          <input
            type="number"
            value={clientTotalQuote}
            onChange={(e) => setClientTotalQuote(e.target.value)}
            className="w-full rounded-2xl border border-slate-300 bg-slate-50 p-2.5 text-slate-950"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm text-slate-600">Deposit Required (£)</label>
          <input
            type="number"
            value={depositRequired}
            onChange={(e) => setDepositRequired(e.target.value)}
            className="w-full rounded-2xl border border-slate-300 bg-slate-50 p-2.5 text-slate-950"
          />
        </div>

        <div className="md:col-span-2">
          <label className="mb-1 block text-sm text-slate-600">Notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full rounded-2xl border border-slate-300 bg-slate-50 p-2.5 text-slate-950"
            rows={3}
          />
        </div>

        <div className="flex items-end md:col-span-2">
          <button
            onClick={(e) => {
              e.preventDefault();
              saveHeader();
            }}
            disabled={saving}
            className="rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
            type="button"
          >
            {saving ? "Saving..." : "Save Commercial Header"}
          </button>
        </div>
      </div>

      <div className="border-t border-slate-200 pt-4">
        <div className="mb-2 text-sm font-semibold text-slate-950">Add Transaction</div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-6">
          <div>
            <label className="mb-1 block text-xs text-slate-600">Type</label>
            <select
              value={txType}
              onChange={(e) => setTxType(e.target.value)}
              className="w-full rounded-2xl border border-slate-300 bg-slate-50 p-2.5 text-slate-950"
            >
              <option value="deposit">deposit</option>
              <option value="payment">payment</option>
              <option value="refund">refund</option>
              <option value="adjustment">adjustment</option>
              <option value="charge">charge</option>
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs text-slate-600">Direction</label>
            <select
              value={txDir}
              onChange={(e) => setTxDir(e.target.value)}
              className="w-full rounded-2xl border border-slate-300 bg-slate-50 p-2.5 text-slate-950"
            >
              <option value="in">in</option>
              <option value="out">out</option>
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs text-slate-600">Amount (£)</label>
            <input
              type="number"
              value={txAmount}
              onChange={(e) => setTxAmount(e.target.value)}
              className="w-full rounded-2xl border border-slate-300 bg-slate-50 p-2.5 text-slate-950"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs text-slate-600">Method</label>
            <input
              value={txMethod}
              onChange={(e) => setTxMethod(e.target.value)}
              placeholder="card/bank/cash"
              className="w-full rounded-2xl border border-slate-300 bg-slate-50 p-2.5 text-slate-950"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs text-slate-600">Reference</label>
            <input
              value={txRef}
              onChange={(e) => setTxRef(e.target.value)}
              placeholder="receipt/invoice id"
              className="w-full rounded-2xl border border-slate-300 bg-slate-50 p-2.5 text-slate-950"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={(e) => {
                e.preventDefault();
                addTx();
              }}
              disabled={txSaving}
              className="w-full rounded-2xl bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-50"
              type="button"
            >
              {txSaving ? "Adding..." : "Add Tx"}
            </button>
          </div>

          <div className="md:col-span-2 lg:col-span-6">
            <label className="mb-1 block text-xs text-slate-600">Notes</label>
            <input
              value={txNotes}
              onChange={(e) => setTxNotes(e.target.value)}
              className="w-full rounded-2xl border border-slate-300 bg-slate-50 p-2.5 text-slate-950"
            />
          </div>
        </div>

        <div className="mt-4">
          <div className="mb-2 text-sm font-semibold text-slate-950">Transactions</div>
          {txList.length === 0 ? (
            <div className="text-sm text-slate-500">No transactions recorded yet.</div>
          ) : (
            <div className="overflow-auto rounded-2xl border border-slate-200 bg-white">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-700">
                  <tr>
                    <th className="px-3 py-2 text-left">When</th>
                    <th className="px-3 py-2 text-left">Type</th>
                    <th className="px-3 py-2 text-left">Dir</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                    <th className="px-3 py-2 text-left">Method</th>
                    <th className="px-3 py-2 text-left">Ref</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {txList
                    .slice()
                    .sort((a, b) => new Date(b?.created_at || 0).getTime() - new Date(a?.created_at || 0).getTime())
                    .map((t, idx) => {
                      const dir = String(t?.direction || "").toLowerCase();
                      const amt = toNum(t?.amount, 0);
                      const signed = dir === "out" ? -amt : amt;
                      return (
                        <tr key={t?.id ?? idx} className="text-slate-800">
                          <td className="px-3 py-2 text-slate-500">
                            {t?.created_at ? asDateTimeGB(t.created_at) : "—"}
                          </td>
                          <td className="px-3 py-2">{safeStr(t?.tx_type)}</td>
                          <td className="px-3 py-2">{safeStr(t?.direction)}</td>
                          <td className={`px-3 py-2 text-right ${signed < 0 ? "text-rose-700" : "text-emerald-700"}`}>
                            {signed < 0 ? `- ${gbp(Math.abs(signed))}` : gbp(signed)}
                          </td>
                          <td className="px-3 py-2 text-slate-600">{safeStr(t?.method, "—")}</td>
                          <td className="px-3 py-2 text-slate-600">{safeStr(t?.reference, "—")}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RevenueForm(props) {
  return <FinancialEventRevenueEntry eventId={props.eventId} onSaved={props.onSaved} legacyFallback={<LegacyRevenueForm {...props} />} />;
}

function LegacyRevenueForm({ eventId, currentFood, currentBeverage, onSaved }) {
  const [foodRevenue, setFoodRevenue] = useState(currentFood ?? "");
  const [beverageRevenue, setBeverageRevenue] = useState(currentBeverage ?? "");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => setFoodRevenue(currentFood ?? ""), [currentFood]);
  useEffect(() => setBeverageRevenue(currentBeverage ?? ""), [currentBeverage]);

  const submit = async () => {
    if (foodRevenue === "" || beverageRevenue === "") {
      toast.error("Please fill in both revenue fields.");
      return;
    }

    const food = Number(foodRevenue);
    const bev = Number(beverageRevenue);

    if (!Number.isFinite(food) || !Number.isFinite(bev)) {
      toast.error("Revenue values must be valid numbers.");
      return;
    }

    setSaving(true);
    setSuccess(false);

    const payload = {
      actual_food_revenue: food,
      actual_beverage_revenue: bev,
      notes,
    };

    try {
      await postEventRevenueWithFallbacks(eventId, payload);
      setSuccess(true);
      toast.success("Revenue updated");
      if (onSaved) await onSaved();
    } catch (err) {
      console.error("Error submitting revenue:", err);
      toast.error(err?.response?.data?.error || err?.message || "Failed to submit revenue");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
      <h2 className="text-xl font-bold text-slate-950">Update Actual Revenue</h2>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm text-slate-600">Food Revenue (£)</label>
          <input
            type="number"
            value={foodRevenue}
            onChange={(e) => setFoodRevenue(e.target.value)}
            className="w-full rounded-2xl border border-slate-300 bg-slate-50 p-2.5 text-slate-950"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm text-slate-600">Beverage Revenue (£)</label>
          <input
            type="number"
            value={beverageRevenue}
            onChange={(e) => setBeverageRevenue(e.target.value)}
            className="w-full rounded-2xl border border-slate-300 bg-slate-50 p-2.5 text-slate-950"
          />
        </div>
      </div>

      <div>
        <label className="mb-1 block text-sm text-slate-600">Notes (optional)</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full rounded-2xl border border-slate-300 bg-slate-50 p-2.5 text-slate-950"
        />
      </div>

      <button
        onClick={submit}
        className="rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
        disabled={saving}
        type="button"
      >
        {saving ? "Saving..." : "Submit Revenue"}
      </button>

      {success && <p className="mt-2 text-sm text-emerald-700">Revenue updated successfully!</p>}
    </div>
  );
}

function StaffingCostManagerPanel({ eventId, staffingCosts, loaded, forecastTotal, onSaved }) {
  const costCentres = Array.isArray(staffingCosts?.costCentres) ? staffingCosts.costCentres : [];
  const actualRows = Array.isArray(staffingCosts?.actualRows) ? staffingCosts.actualRows : [];
  const forecastRows = Array.isArray(staffingCosts?.forecastRows) ? staffingCosts.forecastRows : [];

  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (actualRows.length > 0) {
      setRows(
        actualRows.map((r, idx) => ({
          local_id: `${r.cost_centre_id}-${idx}`,
          cost_centre_id: String(r.cost_centre_id),
          amount: String(toNum(r.amount, 0)),
          notes: r.notes || "",
        }))
      );
      return;
    }

    if (forecastRows.length > 0) {
      setRows(
        forecastRows.map((r, idx) => ({
          local_id: `f-${r.cost_centre_id}-${idx}`,
          cost_centre_id: String(r.cost_centre_id),
          amount: "",
          notes: "",
        }))
      );
      return;
    }

    setRows([]);
  }, [actualRows, forecastRows]);

  const mergedCentres = useMemo(() => {
    const derivedFromForecast = forecastRows.map((r) => ({
      cost_centre_id: Number(r.cost_centre_id),
      code: safeStr(r.cost_centre_code, ""),
      name: safeStr(r.cost_centre_name, "") || `Cost Centre ${r.cost_centre_id}`,
    }));

    const derivedFromActual = actualRows.map((r) => ({
      cost_centre_id: Number(r.cost_centre_id),
      code: safeStr(r.cost_centre_code, ""),
      name: safeStr(r.cost_centre_name, "") || `Cost Centre ${r.cost_centre_id}`,
    }));

    return Array.from(
      new Map(
        [...costCentres, ...derivedFromForecast, ...derivedFromActual]
          .filter((c) => Number.isFinite(Number(c?.cost_centre_id)))
          .map((c) => [Number(c.cost_centre_id), c])
      ).values()
    );
  }, [costCentres, forecastRows, actualRows]);

  const actualTotalDraft = rows.reduce((acc, row) => acc + toNum(row.amount, 0), 0);
  const variance = actualTotalDraft - toNum(forecastTotal, 0);

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      {
        local_id: `new-${Date.now()}-${prev.length}`,
        cost_centre_id: "",
        amount: "",
        notes: "",
      },
    ]);
  };

  const updateRow = (localId, patch) => {
    setRows((prev) => prev.map((r) => (r.local_id === localId ? { ...r, ...patch } : r)));
  };

  const removeRow = (localId) => {
    setRows((prev) => prev.filter((r) => r.local_id !== localId));
  };

  const saveActuals = async () => {
    const cleanRows = rows
      .map((r) => ({
        cost_centre_id: Number(r.cost_centre_id),
        amount: Number(r.amount),
        notes: safeStr(r.notes, ""),
      }))
      .filter(
        (r) => Number.isFinite(r.cost_centre_id) && r.cost_centre_id > 0 && Number.isFinite(r.amount) && r.amount >= 0
      );

    if (!cleanRows.length) {
      toast.error("Add at least one valid staffing cost row.");
      return;
    }

    setSaving(true);
    try {
      await postEventStaffingActualsWithFallbacks(eventId, {
        staffing_actual_lines: cleanRows,
        replace: true,
      });
      toast.success("Staffing actuals updated");
      if (onSaved) await onSaved();
    } catch (e) {
      console.error("Staffing actuals save failed:", e);
      toast.error(e?.response?.data?.error || e?.message || "Failed to save staffing actuals");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-950">Actual Staffing Costs</h2>
          <div className="mt-1 text-sm text-slate-500">
            Enter actual staffing costs by cost centre so management can compare forecast vs actual even when rota is
            incomplete.
          </div>
        </div>
        <div className="text-right text-sm">
          <div className="text-slate-500">Forecast</div>
          <div className="font-semibold text-slate-950">{gbp(forecastTotal)}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <SummaryCardSmall title="Forecast Staffing" value={gbp(forecastTotal)} />
        <SummaryCardSmall title="Actual Staffing (Draft)" value={gbp(actualTotalDraft)} />
        <SummaryCardSmall title="Variance" value={gbp(variance)} />
      </div>

      {!loaded ? <div className="text-sm text-slate-500">Loading staffing cost data…</div> : null}

      {loaded && mergedCentres.length === 0 ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          No cost centres were returned by the event staffing-cost API yet. The UI is ready, but backend still needs to
          return available cost centres for this event.
        </div>
      ) : null}

      <div className="space-y-3">
        {rows.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
            No staffing actual rows yet. Add one below.
          </div>
        ) : null}

        {rows.map((row) => {
          const cc = mergedCentres.find((c) => String(c.cost_centre_id) === String(row.cost_centre_id));
          const forecastForCentre = forecastRows.find((f) => String(f.cost_centre_id) === String(row.cost_centre_id));
          return (
            <div
              key={row.local_id}
              className="grid grid-cols-1 gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 lg:grid-cols-12"
            >
              <div className="lg:col-span-4">
                <label className="mb-1 block text-xs text-slate-600">Cost Centre</label>
                <select
                  value={row.cost_centre_id}
                  onChange={(e) => updateRow(row.local_id, { cost_centre_id: e.target.value })}
                  className="w-full rounded-2xl border border-slate-300 bg-white p-2.5 text-slate-950"
                >
                  <option value="">Select cost centre</option>
                  {mergedCentres.map((centre) => (
                    <option key={centre.cost_centre_id} value={String(centre.cost_centre_id)}>
                      {centre.code ? `${centre.code} · ` : ""}
                      {centre.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="lg:col-span-3">
                <label className="mb-1 block text-xs text-slate-600">Actual Amount (£)</label>
                <input
                  type="number"
                  value={row.amount}
                  onChange={(e) => updateRow(row.local_id, { amount: normalizeMoneyInput(e.target.value) })}
                  className="w-full rounded-2xl border border-slate-300 bg-white p-2.5 text-slate-950"
                />
              </div>

              <div className="lg:col-span-4">
                <label className="mb-1 block text-xs text-slate-600">Notes</label>
                <input
                  value={row.notes}
                  onChange={(e) => updateRow(row.local_id, { notes: e.target.value })}
                  className="w-full rounded-2xl border border-slate-300 bg-white p-2.5 text-slate-950"
                  placeholder="Optional note"
                />
              </div>

              <div className="flex items-end lg:col-span-1">
                <button
                  onClick={() => removeRow(row.local_id)}
                  className="w-full rounded-2xl bg-rose-600 px-3 py-2.5 text-sm font-semibold text-white hover:bg-rose-700"
                  type="button"
                >
                  Remove
                </button>
              </div>

              {(cc || forecastForCentre) && (
                <div className="text-[11px] text-slate-500 lg:col-span-12">
                  {cc ? (
                    <span className="mr-4 text-slate-700">
                      Selected: {cc.code ? `${cc.code} · ` : ""}
                      {cc.name}
                    </span>
                  ) : null}
                  {forecastForCentre ? (
                    <span>
                      Forecast for this centre:{" "}
                      <span className="text-slate-950">{gbp(forecastForCentre.amount)}</span>
                    </span>
                  ) : null}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={addRow}
          className="rounded-2xl bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-sky-700"
          type="button"
        >
          Add Cost Centre Row
        </button>

        <button
          onClick={saveActuals}
          disabled={saving}
          className="rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
          type="button"
        >
          {saving ? "Saving..." : "Save Staffing Actuals"}
        </button>
      </div>
    </div>
  );
}